/**
 * Code a page loads only when a person needs it (0.15.1). The vendored build (scripts/vendorBundle.mjs) makes each of these
 * a file of its own beside gyro-react.mjs, and a host's bundler emits each as a chunk of its own, so a page that never
 * makes a file never downloads the document writers. The custom-element build (gyro-element.js) keeps everything inline,
 * in one file, so there nothing is fetched and nothing can be missing.
 */
/** The vendored files loaded on demand, by what they hold. scripts/vendorBundle.mjs builds exactly these (a test checks). */
export const ON_DEMAND_FILES = {
    /** An inert entry in public builds; the private interface in explicitly enabled builds. */
    companions: 'gyro-react-companions.mjs',
    /** The document generator: picks the writer for a format (documentGenerator.ts). */
    documents: 'gyro-react-documents.mjs',
    /** The PDF writer (pdf-lib and fontkit); its fonts are a file of their own, gyro-react-fonts.mjs (documentPdf.ts). */
    pdf: 'gyro-react-pdf.mjs',
    /** The Word writer (docx). */
    docx: 'gyro-react-docx.mjs',
    /** The Excel writer (exceljs). */
    xlsx: 'gyro-react-xlsx.mjs',
    /** The PowerPoint writer (pptxgenjs). */
    pptx: 'gyro-react-pptx.mjs',
    /** Markdown, text, CSV, TSV, JSON and HTML, and the ZIP and .skill bundles (documentPortable.ts). */
    portable: 'gyro-react-portable.mjs',
    /** The ZIP library (JSZip) for ZIP and .skill bundles. */
    zip: 'gyro-react-zip.mjs',
    /** The web entry's parts a person sees only after an action (webLater.ts). */
    webLater: 'gyro-react-web-later.mjs',
    /** StarScribe's page editor (starscribe/editor.ts), the first time a page opens; never loaded where StarScribe does not run. */
    starscribe: 'gyro-react-starscribe.mjs',
};
/**
 * What the person reads when a document writer did not load. The file stays unmade; nothing else changes. A browser keeps a
 * module it failed to fetch as failed until the page reloads (Chromium does; the HTML module map caches the failure), so
 * the person is told to reload, never only to try again.
 */
export const FILE_WRITER_UNAVAILABLE = 'Gyro could not load what it needs to make this file, so it made none. Reload the page to try again, or choose another format.';
/**
 * A loader that loads `file` the first time it is called and hands every later call the same module. A load that fails
 * (a host that copied gyro-react.mjs alone, a server that does not serve the file, a dropped connection) is never silent:
 * the call rejects with `unavailable` for the person, and the console gets an error that names the file and the cause.
 * The loader keeps nothing from a failed load, so the next call calls `load` again. In a browser that call usually fails
 * at once, without a new request: the browser keeps the failed module until the page reloads. Hence the messages say to
 * reload.
 */
export function onDemand(options) {
    let loaded = null;
    return () => {
        if (loaded)
            return loaded;
        const attempt = (async () => {
            let module;
            let cause;
            try {
                module = await options.load();
            }
            catch (error) {
                cause = error;
            }
            if (module !== undefined && module !== null && options.check(module))
                return module;
            console.error(`[Gyro] ${options.file} ${module ? 'loaded without' : 'did not load, so Gyro could not use'} ${options.what}. `
                + 'The widget loads it from its own folder the first time it is needed: copy the whole vendor-pkg folder, not one '
                + 'file of it, and serve every file in it.', ...(module ? [] : [cause]));
            throw new Error(options.unavailable ?? FILE_WRITER_UNAVAILABLE);
        })();
        loaded = attempt;
        attempt.catch(() => { if (loaded === attempt)
            loaded = null; });
        return attempt;
    };
}
//# sourceMappingURL=onDemand.js.map